import React, { useState, useEffect } from "react";
import { Table, Button, Input, Row, Col, Card, CardBody, Label, FormGroup, FormText, FormFeedback, Container, CardHeader } from "reactstrap";
import { useQuery } from "@tanstack/react-query";
import { getMapToSourceData } from "../data/mapToDataService";
import { isValidExcelCell } from "../../../utils/excelUtils";
import DialogBox from "../../../components/DialogBox";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, useSortable, sortableKeyboardCoordinates, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Collapsable from "../../../components/Collapsable";
import Select from "react-select";
import { useTheme } from "../../../hooks/useTheme";

export default function ConfigEditor({ value, onChange }) {
  const theme = useTheme();
  const [config, setConfig] = useState(() => {
    try {
      return value ? value : { sortOrder: 1, sheets: [] };
    } catch {
      return { sortOrder: 1, sheets: [] };
    }
  });

  const [isDialogBoxOpen, setIsDialogBoxOpen] = useState(false);
  const [tabIndexToRemove, setTabIndexToRemove] = useState(null);

  // Track active sheet by ID for stability
  const [activeId, setActiveId] = useState(null);
  const activeIndex = activeId != null ? config.sheets.findIndex((i) => i.id === activeId) : null;
  const activeSheet = activeIndex != null && activeIndex >= 0 ? config.sheets[activeIndex] : null;

  const toggleActive = (id) => setActiveId(id);

  const [openValidators, setOpenValidators] = useState(false);
  const [openColumns, setOpenColumns] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const customStyles = {
    option: (styles, { isSelected }) => ({
      ...styles,
      fontFamily: "Hanken Grotesk, Sans-serif",
      fontWeight: isSelected ? "bold" : "normal",
      color: isSelected ? "#fff" : "",
      lineHeight: "0.8em"
    })
  };

  const customTheme = {
    ...theme,
    colors: {
      ...theme.colors,
      primary25: "#B2D953",
      primary: theme === "dark-mode" ? "#2c414b" : "#dee2e6",
      neutral0: theme === "dark-mode" ? "#1b272d" : "#E4E4E4"
    }
  };

  const {
    data: mapToSourceData,
    isLoading: isLoadingMapToSourceDataLookup,
    error: mapToSourceDataLookupError
  } = useQuery({
    queryKey: ["mapToSourceDataLookup"],
    queryFn: getMapToSourceData,
    staleTime: 60000
  });

  useEffect(() => {
    onChange(config);
  }, [config]);

  // Ensure something is selected when sheets exist
  useEffect(() => {
    if (activeId == null && config.sheets.length) {
      setActiveId(config.sheets[0].id);
    }
  }, [config.sheets.length, activeId]);

  const addSheet = () => {
    const sheets = [...config.sheets];
    const max = sheets.length ? Math.max(...sheets.map((m) => m.id || 0)) : 0;
    const nextId = max + 1;
    sheets.push({
      id: nextId,
      name: `Sheet${sheets.length + 1}`,
      sortOrder: 1,
      stop: { text: "", column: "", emptyRows: 100 },
      validations: [],
      columns: []
    });
    setConfig((prev) => ({ ...prev, sheets }));
    setActiveId(nextId);
  };

  const handleRemoveTab = (e, idx) => {
    e.stopPropagation();
    setTabIndexToRemove(idx);
    setIsDialogBoxOpen(true);
  };

  const removeTab = () => {
    const sheets = [...config.sheets];
    const removed = sheets.splice(tabIndexToRemove, 1)[0];

    let nextActive = activeId;
    if (removed?.id === activeId) {
      nextActive = sheets[tabIndexToRemove]?.id ?? sheets[tabIndexToRemove - 1]?.id ?? null;
    }

    setConfig((prev) => ({ ...prev, sheets }));
    setActiveId(nextActive);
    setIsDialogBoxOpen(false);
    setTabIndexToRemove(null);
  };

  const handleTabNameChanged = (e) => {
    const sheets = [...config.sheets];
    sheets[activeIndex].name = e.target.value;
    setConfig({ ...config, sheets });
  };

  const handleStopTextChanged = (e) => {
    const sheets = [...config.sheets];
    if (!sheets[activeIndex].stop) sheets[activeIndex].stop = {};
    sheets[activeIndex].stop.text = e.target.value;
    setConfig({ ...config, sheets });
  };

  const handleStopColumnChanged = (e) => {
    const sheets = [...config.sheets];
    if (!sheets[activeIndex].stop) sheets[activeIndex].stop = {};
    sheets[activeIndex].stop.column = e.target.value;
    setConfig({ ...config, sheets });
  };

  const handleStopEmptyRowsChanged = (e) => {
    const v = e.target.value;
    const num = v === "" ? "" : Math.max(1, parseInt(v, 10) || 1);
    const sheets = [...config.sheets];
    if (!sheets[activeIndex].stop) sheets[activeIndex].stop = {};
    sheets[activeIndex].stop.emptyRows = num;
    setConfig({ ...config, sheets });
  };

  const addValidation = () => {
    const sheets = [...config.sheets];
    if (!sheets[activeIndex].validations) sheets[activeIndex].validations = [];
    sheets[activeIndex].validations.push({ cell: "", value: "" });
    setConfig({ ...config, sheets });
  };

  const updateValidation = (valIndex, field, val) => {
    const sheets = [...config.sheets];
    sheets[activeIndex].validations[valIndex][field] = val;
    setConfig({ ...config, sheets });
  };

  const removeValidation = (valIndex) => {
    const sheets = [...config.sheets];
    sheets[activeIndex].validations.splice(valIndex, 1);
    setConfig({ ...config, sheets });
  };

  const addColumn = () => {
    const sheets = [...config.sheets];
    if (!sheets[activeIndex].columns) sheets[activeIndex].columns = [];
    sheets[activeIndex].columns.push({
      name: "",
      type: "row",
      mapTo: "",
      isRequired: false,
      isLink: false,
      operation: "",
      operationValueType: "value",

      conditionType: "ALWAYS",          // ALWAYS | IFCONTAINS | IFNOTCONTAINS
      conditionTarget: "column",         // column | cell
      conditionName: "",                 // header text when target === 'column' OR cell ref when target === 'cell'
      conditionValue: ""                 // text to look for
    });
    setConfig({ ...config, sheets });
  };

  const updateColumn = (colIndex, field, val) => {
    const sheets = [...config.sheets];
    sheets[activeIndex].columns[colIndex][field] = val;
    setConfig({ ...config, sheets });
  };

  const removeColumn = (colIndex) => {
    const sheets = [...config.sheets];
    sheets[activeIndex].columns.splice(colIndex, 1);
    setConfig({ ...config, sheets });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;

    if (active.id !== over.id) {
      const from = config.sheets.findIndex((i) => i.id === active.id);
      const to = config.sheets.findIndex((i) => i.id === over.id);
      setConfig((prev) => ({ ...prev, sheets: arrayMove(prev.sheets, from, to) }));
    }
  };

  const validateNameMappingColumn = (value, type) => {
    if (value && type === "cell" && !isValidExcelCell(value)) {
      return false;
    }
    return true;
  }

  const needsConditionDetail = (c) =>
    c.conditionType === "IFCONTAINS" || c.conditionType === "IFNOTCONTAINS";

  return (
    <div className="modal-body-flex bg p-3 d-flex flex-column" style={{ margin: "-1rem" }}>
      <Row style={{ flex: 1, marginTop: "8px" }}>
        {/* Sheets Area */}
        <Col sm={3} style={{ height: "100%" }}>
          <Card style={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <CardHeader>
              <Row>
                <Col style={{ display: "inline-flex" }}>
                  <h6 className="custom-nav-selected custom-nav m-3" style={{ cursor: "pointer" }}>
                    Sheets
                  </h6>
                </Col>
                <Col className="d-flex align-items-center justify-content-end">
                  <Button onClick={addSheet} className="me-2" size="sm">
                    Add Sheet
                  </Button>
                </Col>
              </Row>
            </CardHeader>
            <CardBody style={{ borderRadius: "4px", display: "flex", flexDirection: "column" }}>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToVerticalAxis]}
              >
                <SortableContext items={config.sheets.map((sheet) => sheet.id)} strategy={verticalListSortingStrategy}>
                  {config.sheets.map((sheet, idx) => (
                    <SortableItem
                      key={sheet.id}
                      id={sheet.id}
                      name={sheet.name}
                      setActive={toggleActive}
                      handleRemoveTab={(e) => handleRemoveTab(e, idx)}
                      activeIndex={idx}
                      currentActive={activeIndex}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </CardBody>
          </Card>
        </Col>

        {/* Editor Panel */}
        <Col sm={9}>
          <Card style={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <CardBody style={{ flex: 1, margin: "1rem -1rem" }}>
              {activeIndex !== null && activeIndex >= 0 && activeSheet && (
                <div style={{ maxHeight: "100%" }}>
                  <Container fluid>
                    <Container fluid>
                      <FormGroup row>
                        <Label sm={2}>Sheet Name</Label>
                        <Col sm={10}>
                          <Input value={activeSheet.name} onChange={handleTabNameChanged} />
                        </Col>
                      </FormGroup>
                      <FormGroup row>
                        <Label sm={2}>Stop Text</Label>
                        <Col sm={3}>
                          <Input
                            value={activeSheet.stop?.text || ""}
                            onChange={handleStopTextChanged}
                          />
                          <FormText>
                            <i className="bi bi-info-circle-fill me-2"></i>
                            Column and specified text to be detected to stop processing of data
                          </FormText>
                        </Col>
                        <Label sm={1}>Column</Label>
                        <Col sm={2}>
                          <Input value={activeSheet.stop?.column || ""} onChange={handleStopColumnChanged} />
                        </Col>
                        <Label sm={2}>Empty Rows</Label>
                        <Col sm={2}>
                          <Input
                            type="number"
                            min={1}
                            step={1}
                            value={activeSheet.stop?.emptyRows ?? 100}
                            onChange={handleStopEmptyRowsChanged}
                          />
                          <FormText>
                            <i className="bi bi-info-circle-fill me-2"></i>
                            Stop after this many consecutive empty rows (defaults to 100).
                          </FormText>
                        </Col>
                      </FormGroup>
                    </Container>
                  </Container>

                  <Collapsable fluid header="Validation Cells" open={openValidators}>
                    <Container>
                      {activeSheet.validations?.length === 0 && <p>No validations defined.</p>}
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <FormText>
                          <i className="bi bi-info-circle-fill me-2"></i>
                          Specify cells that should have specific data in to be a valid template
                        </FormText>
                        <Button style={{ marginBottom: "8px" }} size="sm" onClick={addValidation}>
                          Add New Validation
                        </Button>
                      </div>
                      <br />

                      {activeSheet.validations?.map((validation, valIndex) => (
                        <Row key={valIndex} className="align-items-center mb-2">
                          <Col sm={2}>
                            <Input
                              placeholder="Cell"
                              value={validation.cell}
                              onChange={(e) => updateValidation(valIndex, "cell", e.target.value)}
                            />
                          </Col>
                          <Col sm={9}>
                            <Input
                              placeholder="Value"
                              value={validation.value}
                              onChange={(e) => updateValidation(valIndex, "value", e.target.value)}
                            />
                          </Col>
                          <Col sm={1} className="text-end">
                            <Button color="danger" className="removeButton" size="sm" onClick={() => removeValidation(valIndex)}>
                              <i className="bi bi-trash"></i>
                            </Button>
                          </Col>
                        </Row>
                      ))}
                    </Container>
                  </Collapsable>

                  <Collapsable fluid header="Mapping" open={openColumns}>
                    <Container fluid>
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <FormText>
                          <i className="bi bi-info-circle-fill me-2"></i>
                          Column is for columns in the main table. Cell is for grabbing a specific cell to map to iQuote. Value is for a fixed value.
                          <br /> <span className="ms-4">Link determines if the field should be used to find existing items within the catalogue.</span>
                          <br /> <span className="ms-4">Rows are included only when all required fields have non-zero values.</span>
                          <br /> <span className="ms-4">Operations are applied in the order they appear, left to right.</span>
                          <br /> <span className="ms-4">Conditions allow you to include a column only when certain criteria are met.</span>
                        </FormText>
                        <Button style={{ marginBottom: "8px" }} onClick={addColumn}>
                          Add New Line
                        </Button>
                      </div>
                      <br />
                      <Table bordered responsive size="sm" className="mb-2">
                        <thead>
                          <tr>
                            <th>Name/Value</th>
                            <th>Type</th>
                            <th>Operation</th>
                            <th>Operation Value</th>
                            <th>Map To</th>
                            <th>Condition</th>
                            <th>Cond. Target</th>
                            <th>Cond. Name/Cell</th>
                            <th>Cond. Value</th>
                            <th>Required</th>
                            <th>Link</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activeSheet.columns?.map((column, colIndex) => (
                            <tr key={colIndex}>
                              <td>
                                <Input value={column.name}
                                  onChange={(e) => updateColumn(colIndex, "name", e.target.value)}
                                  invalid={!validateNameMappingColumn(column.name, column.type)}
                                />
                                <FormFeedback>
                                  {column.type === "cell" ? "Invalid cell reference." : "Invalid value."}
                                </FormFeedback>
                              </td>
                              <td>
                                <Input type="select" value={column.type} onChange={(e) => updateColumn(colIndex, "type", e.target.value)}>
                                  <option value="row">column</option>
                                  <option value="cell">cell</option>
                                  <option value="value">value</option>
                                </Input>
                              </td>
                              <td>
                                <Input
                                  type="select"
                                  value={column.operation || ""}
                                  onChange={(e) => updateColumn(colIndex, "operation", e.target.value)}
                                >
                                  <option value="">—</option>
                                  <option value="+">+</option>
                                  <option value="-">-</option>
                                  <option value="*">×</option>
                                  <option value="/">/</option>
                                  <option value="+%">+%</option>
                                  <option value="-%">-%</option>
                                  <option value="margin">margin%</option>
                                </Input>
                              </td>
                              <td>
                                {column.operation ? (
                                  <div className="d-flex gap-2">
                                    {/* Type of RHS: row | cell | value */}
                                    <Input
                                      type="select"
                                      style={{ maxWidth: 130 }}
                                      value={column.operationValueType || "value"}
                                      onChange={(e) => updateColumn(colIndex, "operationValueType", e.target.value)}
                                    >
                                      <option value="row">column</option>
                                      <option value="cell">cell</option>
                                      <option value="value">value</option>
                                    </Input>

                                    {/* RHS editor */}
                                    {(() => {
                                      const t = column.operationValueType || "value";
                                      if (t === "row") {
                                        return (
                                          <Input
                                            type="text"
                                            value={column.operationValue || ""}
                                            onChange={(e) => updateColumn(colIndex, "operationValue", e.target.value)}
                                            placeholder="Header text (e.g. Unit Price)"
                                          />
                                        );
                                      }
                                      if (t === "cell") {
                                        const invalid = column.operationValue && !isValidExcelCell(column.operationValue);
                                        return (
                                          <>
                                            <Input
                                              type="text"
                                              value={column.operationValue || ""}
                                              onChange={(e) => updateColumn(colIndex, "operationValue", e.target.value)}
                                              invalid={!!invalid}
                                              placeholder="Cell ref (e.g. B2)"
                                            />
                                            {invalid && <FormFeedback>Invalid cell reference.</FormFeedback>}
                                          </>
                                        );
                                      }
                                      // value
                                      return (
                                        <Input
                                          type="number"
                                          step="any"
                                          value={column.operationValue || ""}
                                          onChange={(e) => updateColumn(colIndex, "operationValue", e.target.value)}
                                          placeholder={
                                            column.operation === "+%" || column.operation === "-%" || column.operation === "margin"
                                              ? "Percent (e.g. 20)"
                                              : "Number"
                                          }
                                        />
                                      );
                                    })()}
                                  </div>
                                ) : (
                                  <span className="text-muted"> </span>
                                )}
                              </td>
                              <td
                                className="fixed-col-100">
                                <Select
                                  isSearchable
                                  value={
                                    (mapToSourceData || [])
                                      .map((option) => ({ label: option.fieldName, value: option.fieldName }))
                                      .find((f) => f.value === column.mapTo) || null
                                  }
                                  onChange={(option) => updateColumn(colIndex, "mapTo", option?.value ?? "")}
                                  theme={customTheme}
                                  menuPortalTarget={document.body}
                                  menuPosition="fixed"
                                  styles={{
                                    ...customStyles,
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                    menu: (base) => ({ ...base, zIndex: 9999 })
                                  }}
                                  options={(mapToSourceData || [])
                                    .map((option) => ({ label: option.fieldName, value: option.fieldName }))
                                    .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))}
                                />
                              </td>
                              {/* Condition */}
                              <td>
                                <Input
                                  type="select"
                                  value={column.conditionType || "ALWAYS"}
                                  onChange={(e) => updateColumn(colIndex, "conditionType", e.target.value)}
                                >
                                  <option value="ALWAYS">ALWAYS</option>
                                  <option value="IFCONTAINS">IFCONTAINS</option>
                                  <option value="IFNOTCONTAINS">IFNOTCONTAINS</option>
                                </Input>
                              </td>

                              {/* Condition Target */}
                              <td>
                                {needsConditionDetail(column) ? (
                                  <Input
                                    type="select"
                                    value={column.conditionTarget || "column"}
                                    onChange={(e) => updateColumn(colIndex, "conditionTarget", e.target.value)}
                                  >
                                    <option value="column">column</option>
                                    <option value="cell">cell</option>
                                  </Input>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                              {/* Condition Name/Cell */}
                              <td>
                                {(() => {
                                  const t = column.conditionTarget || "column";
                                  const show = needsConditionDetail(column);
                                  if (!show) return <span className="text-muted">—</span>;

                                  if (t === "column") {
                                    return (
                                      <Input
                                        type="text"
                                        placeholder="Header text (e.g. Description)"
                                        value={column.conditionName || ""}
                                        onChange={(e) => updateColumn(colIndex, "conditionName", e.target.value)}
                                      />
                                    );
                                  }
                                  // cell
                                  const invalid = column.conditionName && !isValidExcelCell(column.conditionName);
                                  return (
                                    <>
                                      <Input
                                        placeholder="Cell ref (e.g. B2 or Sheet1!B2)"
                                        value={column.conditionName || ""}
                                        onChange={(e) => updateColumn(colIndex, "conditionName", e.target.value)}
                                        invalid={!!invalid}
                                      />
                                      {invalid && <FormFeedback>Invalid cell reference.</FormFeedback>}
                                    </>
                                  );
                                })()}
                              </td>

                              {/* Condition Value */}
                              <td>
                                {needsConditionDetail(column) ? (
                                  <Input
                                    type="text"
                                    placeholder="Contains text…"
                                    value={column.conditionValue || ""}
                                    onChange={(e) => updateColumn(colIndex, "conditionValue", e.target.value)}
                                  />
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                              <td className="text-center">
                                <Input
                                  type="checkbox"
                                  checked={!!column.isRequired}
                                  onChange={(e) => updateColumn(colIndex, "isRequired", e.target.checked)}
                                />
                              </td>
                              <td className="text-center">
                                <Input
                                  type="checkbox"
                                  checked={!!column.isLink}
                                  onChange={(e) => updateColumn(colIndex, "isLink", e.target.checked)}
                                />
                              </td>
                              <td>
                                <Button color="danger" className="removeButton" size="sm" onClick={() => removeColumn(colIndex)}>
                                  <i className="bi bi-trash"></i>
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Container>
                  </Collapsable>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Remove tab confirmation dialog */}
      <DialogBox
        isOpen={isDialogBoxOpen}
        title="Are you sure you want to delete?"
        message={
          <div className="d-flex align-items-center">
            <i className="bi bi-x-circle-fill text-danger fs-1 me-3"></i>
            <span>Do you want to remove this tab?</span>
          </div>
        }
        toggle={() => setIsDialogBoxOpen(false)}
        buttons={[
          { label: "No", color: "secondary", onClick: () => setIsDialogBoxOpen(false) },
          { label: "Yes", color: "primary", onClick: () => removeTab(tabIndexToRemove) }
        ]}
      />
    </div>
  );
}

function SortableItem(props) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: props.id });

  let style = {
    transform: CSS.Transform.toString(transform),
    transition,
    marginLeft: "-1rem",
    marginRight: "-1rem",
    paddingTop: "0.5rem",
    paddingBottom: "0.5rem",
    paddingLeft: "1rem",
    paddingRight: "1rem",
    cursor: "pointer"
  };

  if (props.activeIndex === props.currentActive) {
    style = {
      ...style,
      backgroundColor: "var(--bg-color-contrast2)"
    };
  }

  return (
    <div onClick={() => props.setActive(props.id)} ref={setNodeRef} style={style}>
      <span
        style={{ cursor: "grab" }}
        className="me-2 text-muted"
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()} // keep row click working
      >
        <i className="bi bi-grip-vertical" />
      </span>
      {props.name}
      <Button
        color="danger"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          props.handleRemoveTab(e, props.activeIndex);
        }}
        style={{ marginRight: "4px", float: "right" }}
      >
        <i className="bi bi-trash"></i>
      </Button>
    </div>
  );
}
